import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { SelectProps } from '@amzn/awsui-components-core';
import { Status } from 'src/models/AuthContextModels';
import { PlanningCycleEntity } from 'src/models/PlanningCycleModel';
import { ValidationStatusEntity } from 'src/models/XptGenericModels';
import { XvtSubmissionRecoveryState, XvtSubmissionRecoveryStorageContext } from '../interfaces/XvtSubmissionInterfaces';
import { XvtGridRowData } from '../xvt-utils/XvtGridFixedFields';

/**
 * xVT filter selections: a key-value map from a filter display name (e.g. "Product Line", "Cost Center") to the
 * selected Cloudscape options. Mirrors forecast's `CorpSegmentFilterSelection` but is xVT-owned.
 */
export interface XvtFilterSelection {
  [filterKey: string]: SelectProps.Options;
}

/**
 * A single dynamic filter dropdown config for the xVT filter bar. Mirrors forecast's
 * `ForecastTemplateCorpSegmentDropdowns` shape but is xVT-owned (no forecast import).
 */
export interface XvtFilterDropdown {
  key: string;
  /** the flat row field this filter applies to (e.g. `product_line`, `cost_center`). */
  fieldKey: string;
  displayName: string;
  isMultiSelect: boolean;
  fieldDropdownOptions: SelectProps.Options;
}

/** Export/sync status for the "Export to OPD Hardware" action. */
export interface XvtExportStatus {
  isExporting: boolean;
  lastExportError?: string;
}

/**
 * Metadata for an xVT snapshot as returned by the `createXVTSnapshot` mutation and the
 * `listXVTSnapshots` query (mirrors the generated `XVTSnapshot` type in `src/API.ts`, kept xVT-local
 * so the feature stays self-contained).
 */
export interface XvtSnapshotMetadata {
  xvt_snapshot_id?: number | null;
  scenario_seq_id?: number | null;
  scenario_name?: string | null;
  planning_cycle_name?: string | null;
  planning_cycle_year?: number | null;
  snapshot_name?: string | null;
  snapshot_note?: string | null;
  snapshot_time?: string | null;
  status?: string | null;
  created_at?: string | null;
  created_by?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

/** Initial validation status - success/neutral until the in-grid validators run. */
export const XVT_INITIAL_VALIDATION_STATUS: ValidationStatusEntity = {
  colorOverride: 'grey',
  validationStatus: 'stopped',
  validationMessage: '',
  validationDefaultMessage: '',
  validationErrorDetails: []
};

export const XVT_NO_SUBMISSION_RECOVERY: XvtSubmissionRecoveryState = {
  status: 'none',
  scenarioSeqId: null,
  failedPhase: null,
  retryEligible: false,
  mutationInput: null,
  message: ''
};

export interface XvtForecastState {
  currentPlanningCycle: PlanningCycleEntity | null;
  xvtFilterSelections: XvtFilterSelection | null;
  xvtHasUnsavedChanges: boolean;

  xvtGridDataLoading: boolean;
  xvtGridDataLoadingRequestId: string | null;
  xvtGridData: XvtGridRowData[];
  xvtFilterDropdowns: XvtFilterDropdown[];
  xvtLoadedScenarioSeqId: number | null;
  xvtLoadedStorageContext: XvtSubmissionRecoveryStorageContext | null;

  xvtSubmitting: boolean;
  xvtSubmitError: string;
  xvtSubmissionRecovery: XvtSubmissionRecoveryState;
  xvtValidationStatus: ValidationStatusEntity;

  exportStatus: XvtExportStatus;
  /** ISO timestamp of the last successful "Export to OPD Hardware", or null if never synced. */
  lastSyncedAt: string | null;
  /** True while a `refreshXVTFromLive` (Refresh from Live R&O) mutation is in flight. */
  refreshingFromLive: boolean;

  /** xVT snapshots for the current business group, loaded via `listXVTSnapshots`. */
  xvtSnapshots: XvtSnapshotMetadata[];
  /** Load status for the `listXVTSnapshots` fetch that backs the snapshots page. */
  xvtSnapshotsLoadStatus: Status;
  /** True while a `createXVTSnapshot` mutation is in flight. */
  xvtSnapshotCreating: boolean;
}

export const XvtForecastInitialState: XvtForecastState = {
  currentPlanningCycle: null,
  xvtFilterSelections: null,
  xvtHasUnsavedChanges: false,

  xvtGridDataLoading: false,
  xvtGridDataLoadingRequestId: null,
  xvtGridData: [],
  xvtFilterDropdowns: [],
  xvtLoadedScenarioSeqId: null,
  xvtLoadedStorageContext: null,

  xvtSubmitting: false,
  xvtSubmitError: '',
  xvtSubmissionRecovery: XVT_NO_SUBMISSION_RECOVERY,
  xvtValidationStatus: XVT_INITIAL_VALIDATION_STATUS,

  exportStatus: { isExporting: false },
  lastSyncedAt: null,
  refreshingFromLive: false,

  xvtSnapshots: [],
  xvtSnapshotsLoadStatus: Status.NotInitiated,
  xvtSnapshotCreating: false
};

const xvtForecastSlice = createSlice({
  name: 'xvtForecast',
  initialState: XvtForecastInitialState,
  reducers: {
    setCurrentPlanningCycle: (state, action: PayloadAction<PlanningCycleEntity | null>) => {
      if (state.currentPlanningCycle?.scenario_seq_id !== action.payload?.scenario_seq_id) {
        state.lastSyncedAt = null;
      }
      state.currentPlanningCycle = action.payload;
    },
    setXvtFilterSelections: (state, action: PayloadAction<XvtFilterSelection | null>) => {
      state.xvtFilterSelections = action.payload as Draft<XvtFilterSelection> | null;
    },
    setXvtHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.xvtHasUnsavedChanges = action.payload;
    },
    startXvtGridDataLoading: (state, action: PayloadAction<string>) => {
      state.xvtGridDataLoading = true;
      state.xvtGridDataLoadingRequestId = action.payload;
    },
    finishXvtGridDataLoading: (state, action: PayloadAction<string>) => {
      if (state.xvtGridDataLoadingRequestId === action.payload) {
        state.xvtGridDataLoading = false;
        state.xvtGridDataLoadingRequestId = null;
      }
    },
    resetXvtGridDataLoading: (state) => {
      state.xvtGridDataLoading = false;
      state.xvtGridDataLoadingRequestId = null;
    },
    setXvtGridData: (state, action: PayloadAction<XvtGridRowData[]>) => {
      state.xvtGridData = action.payload as Draft<XvtGridRowData[]>;
    },
    setXvtFilterDropdowns: (state, action: PayloadAction<XvtFilterDropdown[]>) => {
      state.xvtFilterDropdowns = action.payload as Draft<XvtFilterDropdown[]>;
    },
    setXvtLoadedGridContext: (
      state,
      action: PayloadAction<{ scenarioSeqId: number; storageContext: XvtSubmissionRecoveryStorageContext } | null>
    ) => {
      state.xvtLoadedScenarioSeqId = action.payload?.scenarioSeqId ?? null;
      state.xvtLoadedStorageContext = action.payload?.storageContext ?? null;
    },
    setXvtSubmitting: (state, action: PayloadAction<boolean>) => {
      state.xvtSubmitting = action.payload;
    },
    setXvtSubmitError: (state, action: PayloadAction<string>) => {
      state.xvtSubmitError = action.payload;
    },
    setXvtSubmissionRecovery: (state, action: PayloadAction<XvtSubmissionRecoveryState>) => {
      state.xvtSubmissionRecovery = action.payload as Draft<XvtSubmissionRecoveryState>;
    },
    setXvtValidationStatus: (state, action: PayloadAction<ValidationStatusEntity>) => {
      state.xvtValidationStatus = action.payload;
    },
    setExportStatus: (state, action: PayloadAction<XvtExportStatus>) => {
      state.exportStatus = action.payload;
    },
    setLastSyncedAt: (state, action: PayloadAction<string | null>) => {
      state.lastSyncedAt = action.payload;
    },
    setRefreshFromLiveStatus: (state, action: PayloadAction<boolean>) => {
      state.refreshingFromLive = action.payload;
    },
    setXvtSnapshots: (state, action: PayloadAction<XvtSnapshotMetadata[]>) => {
      state.xvtSnapshots = action.payload;
    },
    setXvtSnapshotsLoadStatus: (state, action: PayloadAction<Status>) => {
      state.xvtSnapshotsLoadStatus = action.payload;
    },
    setXvtSnapshotCreating: (state, action: PayloadAction<boolean>) => {
      state.xvtSnapshotCreating = action.payload;
    }
  }
});

export const {
  setCurrentPlanningCycle,
  setXvtFilterSelections,
  setXvtHasUnsavedChanges,
  startXvtGridDataLoading,
  finishXvtGridDataLoading,
  resetXvtGridDataLoading,
  setXvtGridData,
  setXvtFilterDropdowns,
  setXvtLoadedGridContext,
  setXvtSubmitting,
  setXvtSubmitError,
  setXvtSubmissionRecovery,
  setXvtValidationStatus,
  setExportStatus,
  setLastSyncedAt,
  setRefreshFromLiveStatus,
  setXvtSnapshots,
  setXvtSnapshotsLoadStatus,
  setXvtSnapshotCreating
} = xvtForecastSlice.actions;

export default xvtForecastSlice.reducer;
